import _ from 'lodash';

import { Dropoff, Load, Pickup } from 'types/Load';
import { MaybeUndef } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';
import { formatLoadInstantForDisplay } from 'utils/time';

const SUMMARY_DATETIME_FORMAT = 'MM/DD/YYYY HH:mm';

// Builds a plaintext load summary for clipboard copy (ENG-7885).
// Omits empty lines. First pickup + last delivery only (no intermediate stops).
// equipmentName overrides transportType when that is a TMS record ID (Revenova).
export function buildLoadSummary(
  load: Load,
  tmsName: TMS,
  equipmentName?: string
): string {
  const lines: string[] = [];

  const proId = load.freightTrackingID?.trim() || load.externalTMSID?.trim();
  if (proId) {
    lines.push(`PRO: ${proId}`);
  }

  const pickupLine = formatStopLine('Pickup', load.pickup, tmsName, 'ready');
  if (pickupLine) {
    lines.push(pickupLine);
  }

  const deliveryLine = formatStopLine(
    'Delivery',
    load.consignee,
    tmsName,
    'mustDeliver'
  );
  if (deliveryLine) {
    lines.push(deliveryLine);
  }

  const equipment =
    tmsName === TMS.MercurygateBoomi
      ? load.specifications?.transportSize?.trim() ||
        load.specifications?.transportType?.trim() ||
        ''
      : equipmentName || load.specifications?.transportType?.trim() || '';
  if (equipment) {
    lines.push(`Equipment: ${equipment}`);
  }

  const commodity = resolveCommodity(load);
  if (commodity) {
    lines.push(`Commodity: ${commodity}`);
  }

  const weight = resolveWeight(load);
  if (weight) {
    lines.push(`Weight: ${weight}`);
  }

  appendMcLeodLoadboardComments(lines, load, tmsName);

  return lines.join('\n');
}

function resolveCommodity(load: Load): string {
  const fromSpecs = load.specifications?.commodities?.trim();
  if (fromSpecs) {
    return fromSpecs;
  }

  const fromItems = (load.commodities || [])
    .map((c) => c?.description?.trim())
    .filter(Boolean);
  return fromItems.join(', ');
}

function resolveWeight(load: Load): string {
  const totalWeight = load.specifications?.totalWeight;
  if (!totalWeight || totalWeight.val == null) {
    return '';
  }
  const unit = totalWeight.unit?.trim();
  return unit ? `${totalWeight.val} ${unit}` : String(totalWeight.val);
}

function appendMcLeodLoadboardComments(
  lines: string[],
  load: Load,
  tmsName: TMS
): void {
  if (tmsName !== TMS.McleodEnterprise) {
    return;
  }

  const comment1 = load.specifications?.pnnComment?.trim();
  if (comment1) {
    lines.push(`Comment 1: ${comment1}`);
  }

  const comment2 = load.specifications?.pnnComment2?.trim();
  if (comment2) {
    lines.push(`Comment 2: ${comment2}`);
  }
}

function formatStopLine(
  label: 'Pickup' | 'Delivery',
  stop: MaybeUndef<Pickup | Dropoff>,
  tmsName: TMS,
  plannedKind: 'ready' | 'mustDeliver'
): string {
  if (!stop) {
    return '';
  }

  const location = formatStopLocation(stop);
  const window = formatStopWindow(stop, tmsName, plannedKind);
  const details = [location, window].filter(Boolean).join(' — ');
  if (!details) {
    return '';
  }
  return `${label}: ${details}`;
}

function formatStopLocation(stop: Pickup | Dropoff): string {
  if (stop.city && stop.state) {
    return `${_.startCase(_.toLower(stop.city))}, ${stop.state}`;
  }
  return stop.city?.trim() || stop.name?.trim() || '';
}

function formatStopWindow(
  stop: Pickup | Dropoff,
  tmsName: TMS,
  plannedKind: 'ready' | 'mustDeliver'
): string {
  const plannedStart =
    plannedKind === 'ready'
      ? (stop as Pickup).readyTime
      : (stop as Dropoff).mustDeliver;
  const plannedEnd =
    plannedKind === 'ready'
      ? (stop as Pickup).readyTimeEnd
      : (stop as Dropoff).mustDeliverEnd;

  const { start, end } = resolveStopWindowTimes(
    plannedStart,
    plannedEnd,
    stop.apptStartTime,
    stop.apptEndTime
  );

  if (!start && !end) {
    return '';
  }

  const formatOpts = {
    tmsName,
    stopTimezone: stop.timezone,
    format: SUMMARY_DATETIME_FORMAT,
  };

  const startText = start ? formatLoadInstantForDisplay(start, formatOpts) : '';
  const endText = end ? formatLoadInstantForDisplay(end, formatOpts) : '';

  if (startText && endText) {
    // Same calendar day → "09/02/2026 08:00–12:00"
    const startDay = startText.slice(0, 10);
    const endDay = endText.slice(0, 10);
    if (startDay === endDay && endText.length > 11) {
      return `${startText}–${endText.slice(11)}`;
    }
    return `${startText}–${endText}`;
  }

  return startText || endText;
}

interface StopWindowTimes {
  start: MaybeUndef<string>;
  end: MaybeUndef<string>;
}

// Prefer a complete planned or appointment window; never mix start/end across sources.
function resolveStopWindowTimes(
  plannedStart: MaybeUndef<string>,
  plannedEnd: MaybeUndef<string>,
  apptStart: MaybeUndef<string>,
  apptEnd: MaybeUndef<string>
): StopWindowTimes {
  const pStart = firstMeaningfulTimestamp(plannedStart);
  const pEnd = firstMeaningfulTimestamp(plannedEnd);
  const aStart = firstMeaningfulTimestamp(apptStart);
  const aEnd = firstMeaningfulTimestamp(apptEnd);

  const hasCompletePlanned = pStart != null && pEnd != null;
  const hasCompleteAppt = aStart != null && aEnd != null;

  if (hasCompletePlanned) {
    const plannedWindow: StopWindowTimes = { start: pStart, end: pEnd };
    return plannedWindow;
  }
  if (hasCompleteAppt) {
    const appointmentWindow: StopWindowTimes = { start: aStart, end: aEnd };
    return appointmentWindow;
  }

  const hasAnyPlanned = pStart != null || pEnd != null;
  const hasAnyAppt = aStart != null || aEnd != null;

  if (hasAnyPlanned && !hasAnyAppt) {
    const plannedPartial: StopWindowTimes = { start: pStart, end: pEnd };
    return plannedPartial;
  }
  if (hasAnyAppt && !hasAnyPlanned) {
    const appointmentPartial: StopWindowTimes = { start: aStart, end: aEnd };
    return appointmentPartial;
  }
  if (hasAnyPlanned) {
    const plannedFallback: StopWindowTimes = { start: pStart, end: pEnd };
    return plannedFallback;
  }

  const appointmentFallback: StopWindowTimes = { start: aStart, end: aEnd };
  return appointmentFallback;
}

// Go/protobuf empty UTC datetimes must not count as real stop windows.
const GO_ZERO_UTC_DATETIME_SENTINEL = '0001-01-01T00:00:00Z';
const GO_ZERO_UTC_DATETIME_SENTINEL_MS = '0001-01-01T00:00:00.000Z';

function isMeaningfulTimestamp(value: MaybeUndef<string>): value is string {
  if (typeof value !== 'string') {
    return false;
  }
  const trimmed = value.trim();
  if (trimmed === '') {
    return false;
  }
  return (
    trimmed !== GO_ZERO_UTC_DATETIME_SENTINEL &&
    trimmed !== GO_ZERO_UTC_DATETIME_SENTINEL_MS
  );
}

function firstMeaningfulTimestamp(
  ...values: Array<MaybeUndef<string>>
): MaybeUndef<string> {
  for (const value of values) {
    if (isMeaningfulTimestamp(value)) {
      return value.trim();
    }
  }
  return undefined;
}
